import { useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

const WORLD = {
  width: 960,
  height: 540,
  ground: 420
};

const initialGame = () => ({
  status: 'ready',
  score: 0,
  distance: 0,
  coins: 0,
  speed: 340,
  spawnTimer: 0,
  coinTimer: 0,
  shake: 0,
  player: {
    x: 154,
    y: WORLD.ground - 60,
    w: 42,
    h: 60,
    vy: 0,
    grounded: true,
    sliding: false,
    slideTimer: 0,
    coyote: 0,
    jumpBuffer: 0,
    step: 0,
    facing: 1
  },
  obstacles: [],
  pickups: [],
  clouds: [
    { x: 80, y: 86, s: 0.65 },
    { x: 390, y: 58, s: 1 },
    { x: 720, y: 112, s: 0.78 }
  ],
  buildings: Array.from({ length: 12 }, (_, index) => ({
    x: index * 92,
    w: 58 + (index % 4) * 12,
    h: 82 + (index % 5) * 18
  }))
});

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const rectsOverlap = (a, b) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

const drawPixelRect = (ctx, x, y, w, h, color, unit = 4) => {
  ctx.fillStyle = color;
  ctx.fillRect(Math.round(x / unit) * unit, Math.round(y / unit) * unit, Math.round(w / unit) * unit, Math.round(h / unit) * unit);
};

const drawText = (ctx, text, x, y, size, color, align = 'left') => {
  ctx.fillStyle = color;
  ctx.font = `700 ${size}px "Courier New", monospace`;
  ctx.textAlign = align;
  ctx.textBaseline = 'top';
  ctx.fillText(text, x, y);
};

const PixelRun = () => {
  const canvasRef = useRef(null);
  const gameRef = useRef(initialGame());
  const keysRef = useRef({ left: false, right: false, down: false });
  const frameRef = useRef(0);
  const lastRef = useRef(0);
  const [hud, setHud] = useState({ status: 'ready', score: 0, coins: 0, best: 0 });

  useEffect(() => {
    const best = Number(localStorage.getItem('pixel-run-best') || 0);
    setHud((current) => ({ ...current, best }));
  }, []);

  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const parent = canvas.parentElement;
      const style = window.getComputedStyle(parent);
      const paddingX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      const paddingY = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      const width = parent.clientWidth - paddingX;
      const height = parent.clientHeight - paddingY;
      const scale = Math.min(width / WORLD.width, height / WORLD.height);
      canvas.style.width = `${Math.floor(WORLD.width * scale)}px`;
      canvas.style.height = `${Math.floor(WORLD.height * scale)}px`;
    };

    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (['Space', 'ArrowUp', 'KeyW'].includes(event.code)) {
        event.preventDefault();
        pressJump();
      }
      if (['ArrowLeft', 'KeyA'].includes(event.code)) {
        event.preventDefault();
        keysRef.current.left = true;
      }
      if (['ArrowRight', 'KeyD'].includes(event.code)) {
        event.preventDefault();
        keysRef.current.right = true;
      }
      if (['ArrowDown', 'KeyS'].includes(event.code)) {
        event.preventDefault();
        keysRef.current.down = true;
      }
      if (event.code === 'KeyP') {
        togglePause();
      }
      if (event.code === 'Enter') {
        startGame();
      }
    };

    const onKeyUp = (event) => {
      if (['ArrowLeft', 'KeyA'].includes(event.code)) {
        keysRef.current.left = false;
      }
      if (['ArrowRight', 'KeyD'].includes(event.code)) {
        keysRef.current.right = false;
      }
      if (['ArrowDown', 'KeyS'].includes(event.code)) {
        keysRef.current.down = false;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
    // Game controls read mutable refs so key listeners can stay mounted once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loop = (time) => {
      const delta = Math.min(0.033, (time - lastRef.current) / 1000 || 0);
      lastRef.current = time;
      update(delta);
      render();
      frameRef.current = requestAnimationFrame(loop);
    };

    frameRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frameRef.current);
    // The animation loop owns per-frame mutable state through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncHud = () => {
    const game = gameRef.current;
    const best = Number(localStorage.getItem('pixel-run-best') || 0);
    setHud({ status: game.status, score: Math.floor(game.score), coins: game.coins, best });
  };

  const startGame = () => {
    gameRef.current = initialGame();
    gameRef.current.status = 'running';
    syncHud();
  };

  const togglePause = () => {
    const game = gameRef.current;
    if (game.status === 'running') game.status = 'paused';
    else if (game.status === 'paused') game.status = 'running';
    syncHud();
  };

  const pressJump = () => {
    const game = gameRef.current;
    if (game.status !== 'running') {
      startGame();
      return;
    }
    game.player.jumpBuffer = 0.14;
  };

  const pressSlide = (active) => {
    keysRef.current.down = active;
    const game = gameRef.current;
    if (game.status !== 'running' && active) startGame();
  };

  const pressMove = (direction, active) => {
    keysRef.current[direction] = active;
    const game = gameRef.current;
    if (game.status !== 'running' && active) startGame();
  };

  const spawnObstacle = (game) => {
    const roll = Math.random();
    if (roll > 0.76) {
      game.obstacles.push({ type: 'drone', x: WORLD.width + 30, y: WORLD.ground - 122, w: 50, h: 34 });
    } else if (roll > 0.48) {
      game.obstacles.push({ type: 'stack', x: WORLD.width + 30, y: WORLD.ground - 76, w: 46, h: 76 });
    } else {
      game.obstacles.push({ type: 'box', x: WORLD.width + 30, y: WORLD.ground - 44, w: 52, h: 44 });
    }
  };

  const spawnCoins = (game) => {
    const baseY = WORLD.ground - 122 - Math.random() * 88;
    for (let i = 0; i < 5; i += 1) {
      game.pickups.push({ x: WORLD.width + 26 + i * 30, y: baseY + Math.sin(i) * 18, w: 18, h: 18, taken: false });
    }
  };

  const update = (delta) => {
    const game = gameRef.current;
    const player = game.player;

    if (game.status !== 'running') {
      player.step += delta * 5;
      return;
    }

    game.distance += game.speed * delta;
    game.score += delta * (game.speed * 0.08) + game.coins * 0.003;
    game.speed = clamp(340 + game.distance * 0.018, 340, 690);
    game.spawnTimer -= delta;
    game.coinTimer -= delta;
    game.shake = Math.max(0, game.shake - delta * 12);

    const horizontal = (keysRef.current.right ? 1 : 0) - (keysRef.current.left ? 1 : 0);
    const moveSpeed = player.sliding ? 210 : 330;
    if (horizontal !== 0) {
      player.x = clamp(player.x + horizontal * moveSpeed * delta, 54, WORLD.width - player.w - 42);
      player.facing = horizontal;
    }

    if (game.spawnTimer <= 0) {
      spawnObstacle(game);
      game.spawnTimer = 0.78 + Math.random() * 0.9 - clamp((game.speed - 340) / 900, 0, 0.28);
    }

    if (game.coinTimer <= 0) {
      spawnCoins(game);
      game.coinTimer = 1.4 + Math.random() * 1.3;
    }

    player.coyote = player.grounded ? 0.1 : Math.max(0, player.coyote - delta);
    player.jumpBuffer = Math.max(0, player.jumpBuffer - delta);
    player.sliding = keysRef.current.down && player.grounded;
    player.slideTimer = player.sliding ? player.slideTimer + delta : 0;
    player.h = player.sliding ? 36 : 60;
    player.y = player.grounded ? WORLD.ground - player.h : player.y;

    if (player.jumpBuffer > 0 && (player.grounded || player.coyote > 0)) {
      player.vy = -690;
      player.grounded = false;
      player.coyote = 0;
      player.jumpBuffer = 0;
    }

    player.vy += 1850 * delta;
    player.y += player.vy * delta;
    if (player.y + player.h >= WORLD.ground) {
      player.y = WORLD.ground - player.h;
      player.vy = 0;
      player.grounded = true;
    } else {
      player.grounded = false;
    }
    player.step += delta * (player.grounded ? game.speed / 56 + Math.abs(horizontal) * 9 : 7);

    game.obstacles.forEach((obstacle) => {
      obstacle.x -= game.speed * delta;
    });
    game.pickups.forEach((pickup) => {
      pickup.x -= game.speed * delta;
    });
    game.clouds.forEach((cloud) => {
      cloud.x -= delta * 18 * cloud.s;
      if (cloud.x < -160) cloud.x = WORLD.width + 80;
    });
    game.buildings.forEach((building) => {
      building.x -= delta * game.speed * 0.28;
      if (building.x < -120) building.x += 12 * 92;
    });

    game.obstacles = game.obstacles.filter((obstacle) => obstacle.x > -120);
    game.pickups = game.pickups.filter((pickup) => pickup.x > -60 && !pickup.taken);

    const hurtBox = {
      x: player.x + 8,
      y: player.y + 8,
      w: player.w - 14,
      h: player.h - 10
    };

    for (const obstacle of game.obstacles) {
      const obstacleBox = {
        x: obstacle.x + 4,
        y: obstacle.y + 4,
        w: obstacle.w - 8,
        h: obstacle.h - 8
      };
      if (rectsOverlap(hurtBox, obstacleBox)) {
        game.status = 'ended';
        game.shake = 1;
        const score = Math.floor(game.score);
        const best = Number(localStorage.getItem('pixel-run-best') || 0);
        if (score > best) localStorage.setItem('pixel-run-best', String(score));
        syncHud();
        return;
      }
    }

    for (const pickup of game.pickups) {
      if (rectsOverlap(hurtBox, pickup)) {
        pickup.taken = true;
        game.coins += 1;
        game.score += 24;
      }
    }

    if (Math.floor(game.score) !== hud.score || game.coins !== hud.coins) {
      syncHud();
    }
  };

  const renderBackground = (ctx, game) => {
    const cycle = (Math.sin(game.distance / 2400) + 1) / 2;
    const skyTop = cycle > 0.54 ? '#10264f' : '#65c8f2';
    const skyBottom = cycle > 0.54 ? '#312852' : '#f6d47c';

    const gradient = ctx.createLinearGradient(0, 0, 0, WORLD.height);
    gradient.addColorStop(0, skyTop);
    gradient.addColorStop(1, skyBottom);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, WORLD.width, WORLD.height);

    ctx.fillStyle = cycle > 0.54 ? '#ffe88a' : '#fff3b0';
    ctx.fillRect(770, 64, 44, 44);
    ctx.fillStyle = cycle > 0.54 ? '#10264f' : '#ffd36f';
    ctx.fillRect(756, 58, 28, 28);

    game.clouds.forEach((cloud) => {
      ctx.fillStyle = cycle > 0.54 ? 'rgba(210, 221, 255, 0.28)' : 'rgba(255, 255, 255, 0.72)';
      drawPixelRect(ctx, cloud.x, cloud.y, 62 * cloud.s, 18 * cloud.s, ctx.fillStyle, 2);
      drawPixelRect(ctx, cloud.x + 20 * cloud.s, cloud.y - 12 * cloud.s, 48 * cloud.s, 24 * cloud.s, ctx.fillStyle, 2);
      drawPixelRect(ctx, cloud.x + 54 * cloud.s, cloud.y + 4 * cloud.s, 44 * cloud.s, 18 * cloud.s, ctx.fillStyle, 2);
    });

    game.buildings.forEach((building, index) => {
      const y = WORLD.ground - 58 - building.h;
      ctx.fillStyle = index % 2 ? 'rgba(29, 57, 89, 0.42)' : 'rgba(21, 45, 75, 0.36)';
      ctx.fillRect(building.x, y, building.w, building.h);
      ctx.fillStyle = cycle > 0.54 ? '#ffd86b' : 'rgba(255, 255, 255, 0.38)';
      for (let wy = y + 18; wy < y + building.h - 12; wy += 24) {
        for (let wx = building.x + 12; wx < building.x + building.w - 10; wx += 20) {
          if ((wx + wy + index) % 3 !== 0) ctx.fillRect(wx, wy, 8, 8);
        }
      }
    });

    ctx.fillStyle = '#1f2f37';
    ctx.fillRect(0, WORLD.ground, WORLD.width, WORLD.height - WORLD.ground);
    ctx.fillStyle = '#354c49';
    ctx.fillRect(0, WORLD.ground, WORLD.width, 10);
    ctx.fillStyle = '#8fd14f';
    for (let x = -((game.distance * 0.8) % 48); x < WORLD.width; x += 48) {
      ctx.fillRect(x, WORLD.ground - 8, 28, 8);
      ctx.fillRect(x + 34, WORLD.ground - 4, 10, 4);
    }
    ctx.fillStyle = '#162126';
    for (let x = -((game.distance * 1.6) % 64); x < WORLD.width; x += 64) {
      ctx.fillRect(x, WORLD.ground + 44, 36, 8);
    }
  };

  const renderPlayer = (ctx, player) => {
    const leg = Math.sin(player.step) > 0 ? 1 : -1;
    const x = player.x;
    const y = player.y;
    const suit = '#38d6a5';
    const shade = '#178b78';
    const skin = '#ffd2a1';
    const hair = '#293044';
    const eyeX = player.facing < 0 ? x + 16 : x + 28;

    if (player.sliding) {
      drawPixelRect(ctx, x + 4, y + 6, 46, 24, suit);
      drawPixelRect(ctx, x + 34, y - 4, 22, 22, skin);
      drawPixelRect(ctx, x + 36, y - 8, 20, 8, hair);
      drawPixelRect(ctx, x - 4, y + 24, 24, 8, '#26323d');
      drawPixelRect(ctx, x + 14, y + 30, 34, 8, '#26323d');
      drawPixelRect(ctx, x + 12, y + 10, 10, 8, shade);
      return;
    }

    drawPixelRect(ctx, x + 10, y + 8, 26, 28, suit);
    drawPixelRect(ctx, x + 12, y - 12, 24, 24, skin);
    drawPixelRect(ctx, x + 10, y - 16, 28, 10, hair);
    drawPixelRect(ctx, eyeX, y - 2, 6, 6, '#101820');
    drawPixelRect(ctx, x + 6, y + 16, 10, 28, shade);
    drawPixelRect(ctx, x + 34, y + 16, 10, 24, shade);
    drawPixelRect(ctx, x + 14 + leg * 5, y + 36, 10, 26, '#26323d');
    drawPixelRect(ctx, x + 28 - leg * 5, y + 36, 10, 26, '#26323d');
    drawPixelRect(ctx, x + 10 + leg * 5, y + 58, 18, 8, '#111820');
    drawPixelRect(ctx, x + 24 - leg * 5, y + 58, 18, 8, '#111820');
    drawPixelRect(ctx, x + 17, y + 18, 16, 10, '#f7f26b');
  };

  const renderObstacle = (ctx, obstacle, distance) => {
    if (obstacle.type === 'drone') {
      drawPixelRect(ctx, obstacle.x, obstacle.y + Math.sin(distance / 80) * 5, obstacle.w, obstacle.h, '#e84d5b');
      drawPixelRect(ctx, obstacle.x + 12, obstacle.y + 10, 26, 12, '#421c32');
      drawPixelRect(ctx, obstacle.x - 10, obstacle.y + 8, 12, 8, '#f7f26b');
      drawPixelRect(ctx, obstacle.x + obstacle.w - 2, obstacle.y + 8, 12, 8, '#f7f26b');
      return;
    }
    if (obstacle.type === 'stack') {
      drawPixelRect(ctx, obstacle.x, obstacle.y, obstacle.w, obstacle.h, '#a855f7');
      drawPixelRect(ctx, obstacle.x + 6, obstacle.y + 8, obstacle.w - 12, 10, '#c084fc');
      drawPixelRect(ctx, obstacle.x + 8, obstacle.y + 34, obstacle.w - 16, 10, '#7e22ce');
      drawPixelRect(ctx, obstacle.x + 4, obstacle.y + obstacle.h - 10, obstacle.w - 8, 8, '#42235e');
      return;
    }
    drawPixelRect(ctx, obstacle.x, obstacle.y, obstacle.w, obstacle.h, '#f59e0b');
    drawPixelRect(ctx, obstacle.x + 8, obstacle.y + 8, 14, 14, '#fef3c7');
    drawPixelRect(ctx, obstacle.x + 30, obstacle.y + 18, 12, 12, '#b45309');
  };

  const render = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const game = gameRef.current;

    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, WORLD.width, WORLD.height);
    if (game.shake > 0) {
      ctx.translate((Math.random() - 0.5) * 8 * game.shake, (Math.random() - 0.5) * 8 * game.shake);
    }

    renderBackground(ctx, game);
    game.pickups.forEach((pickup, index) => {
      const bob = Math.sin(game.distance / 90 + index) * 4;
      drawPixelRect(ctx, pickup.x + 4, pickup.y + bob, 10, 18, '#facc15', 2);
      drawPixelRect(ctx, pickup.x + 7, pickup.y + 3 + bob, 4, 12, '#fff7ad', 2);
    });
    game.obstacles.forEach((obstacle) => renderObstacle(ctx, obstacle, game.distance));
    renderPlayer(ctx, game.player);

    drawText(ctx, `SCORE ${Math.floor(game.score).toString().padStart(5, '0')}`, 28, 24, 24, '#f8fafc');
    drawText(ctx, `COINS ${game.coins}`, 28, 56, 20, '#fde68a');
    drawText(ctx, `BEST ${hud.best}`, WORLD.width - 28, 24, 20, '#e2e8f0', 'right');

    if (game.status !== 'running') {
      ctx.fillStyle = 'rgba(8, 13, 24, 0.58)';
      ctx.fillRect(0, 0, WORLD.width, WORLD.height);
      const title = game.status === 'ended' ? 'GAME OVER' : game.status === 'paused' ? 'PAUSED' : 'PIXEL RUN';
      const subtitle = game.status === 'ended' ? 'PRESS ENTER OR TAP TO RETRY' : 'ARROWS / WASD TO MOVE, SPACE TO JUMP';
      drawText(ctx, title, WORLD.width / 2, 166, 56, '#ffffff', 'center');
      drawText(ctx, subtitle, WORLD.width / 2, 240, 24, '#c7f9e7', 'center');
      drawText(ctx, 'RUN THROUGH THE NEON HOTEL ROOFTOPS', WORLD.width / 2, 280, 18, '#f8d66d', 'center');
    }

    ctx.restore();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#101820',
        color: '#f8fafc',
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        overflow: 'hidden'
      }}
    >
      <Box
        component="header"
        sx={{
          px: { xs: 2, md: 4 },
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderBottom: '1px solid rgba(255,255,255,0.12)',
          bgcolor: 'rgba(16,24,32,0.92)'
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontFamily: '"Courier New", monospace', fontWeight: 900, lineHeight: 1 }}>
            像素跑酷
          </Typography>
          <Typography variant="body2" sx={{ color: '#a7f3d0', mt: 0.5 }}>
            A/← 后退，D/→ 前进，W/↑/空格 跳跃，S/↓ 下滑，P 暂停
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button color="success" variant="contained" onClick={startGame}>
            开始
          </Button>
          <Button color="inherit" variant="outlined" onClick={togglePause} sx={{ color: '#f8fafc', borderColor: 'rgba(255,255,255,0.32)' }}>
            暂停
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          minHeight: 0,
          display: 'grid',
          placeItems: 'center',
          p: { xs: 1, md: 2 },
          background:
            'linear-gradient(180deg, rgba(56,214,165,0.12), rgba(16,24,32,0) 34%), radial-gradient(circle at 50% 110%, rgba(250,204,21,0.16), transparent 34%)'
        }}
        onPointerDown={(event) => {
          if (event.target === event.currentTarget) pressJump();
        }}
      >
        <canvas
          ref={canvasRef}
          width={WORLD.width}
          height={WORLD.height}
          aria-label="像素跑酷游戏画布"
          role="img"
          onPointerDown={pressJump}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            border: '4px solid #e2e8f0',
            boxShadow: '0 24px 80px rgba(0,0,0,0.42)',
            background: '#65c8f2',
            imageRendering: 'pixelated',
            touchAction: 'manipulation'
          }}
        />
      </Box>

      <Box
        component="footer"
        sx={{
          display: { xs: 'grid', md: 'none' },
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 1,
          p: 1.5,
          borderTop: '1px solid rgba(255,255,255,0.12)',
          bgcolor: '#101820'
        }}
      >
        <Button
          variant="contained"
          color="inherit"
          onPointerDown={() => pressMove('left', true)}
          onPointerUp={() => pressMove('left', false)}
          onPointerCancel={() => pressMove('left', false)}
          onPointerLeave={() => pressMove('left', false)}
          sx={{ minHeight: 56, fontWeight: 800, color: '#101820' }}
        >
          ←
        </Button>
        <Button variant="contained" color="success" onPointerDown={pressJump} sx={{ minHeight: 56, fontWeight: 800 }}>
          ↑
        </Button>
        <Button
          variant="contained"
          color="inherit"
          onPointerDown={() => pressMove('right', true)}
          onPointerUp={() => pressMove('right', false)}
          onPointerCancel={() => pressMove('right', false)}
          onPointerLeave={() => pressMove('right', false)}
          sx={{ minHeight: 56, fontWeight: 800, color: '#101820' }}
        >
          →
        </Button>
        <Button
          variant="contained"
          color="warning"
          onPointerDown={() => pressSlide(true)}
          onPointerUp={() => pressSlide(false)}
          onPointerCancel={() => pressSlide(false)}
          onPointerLeave={() => pressSlide(false)}
          sx={{ minHeight: 56, fontWeight: 800 }}
        >
          ↓
        </Button>
      </Box>
    </Box>
  );
};

export default PixelRun;
